import { useEffect, useMemo, useRef, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useSnackbar } from "notistack";
import { useAddInfluencers } from "../../hooks/usePlanSummary";
import { parseProfilesInput } from "./parseProfilesInput";
import { isMutationPending } from "./mutationState";
import { PLAN_PLATFORMS, getPlatform, platformLabel } from "./platforms";

const sectionLabelSx = {
  mb: 1,
  color: "#374151",
  fontWeight: 600,
  fontSize: 13.5,
};

// Four platforms on one row: equal-width tiles with the icon above the label, so even
// "Instagram" fits the narrowest (440px) drawer without wrapping onto a second line.
const platformGroupSx = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 1,
  mb: 1.5,
  // ToggleButtonGroup joins its buttons (shared borders, squared inner corners);
  // these are separate tiles, so give each one its own full border and radius.
  "& .MuiToggleButtonGroup-grouped, & .MuiToggleButtonGroup-grouped:not(:first-of-type), & .MuiToggleButtonGroup-grouped:not(:last-of-type)": {
    m: 0,
    border: "1.5px solid #E5E7EB",
    borderRadius: "10px",
  },
  "& .MuiToggleButtonGroup-grouped:hover": { borderColor: "#D1D5DB" },
  "& .MuiToggleButtonGroup-grouped.Mui-selected": { borderColor: "transparent" },
};

const platformButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: { xs: 12.5, sm: 13.5 },
  lineHeight: 1.2,
  color: "#374151",
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
  minWidth: 0,
  px: 0.5,
  py: 1.25,
  whiteSpace: "nowrap",
  transition: "all 0.2s ease",
  "&:hover": { background: "#F9FAFB" },
  "&.Mui-selected": {
    background: "linear-gradient(135deg, #160647 0%, #2A1068 100%)",
    color: "#fff",
    boxShadow: "0 4px 12px rgba(22,6,71,0.25)",
    "&:hover": {
      background: "linear-gradient(135deg, #160647 0%, #2A1068 100%)",
    },
  },
};

// `mutation` lets another plan-backed screen reuse this exact paste-handles
// flow against its own endpoint (Script Approval does) — it just has to answer
// with the same {added, failed, duplicatesInPlan?} shape as
// POST /plans/:planId/influencers. Defaults to adding to the plan itself.
const AddInfluencersDrawer = ({
  open,
  onClose,
  lists = [],
  defaultListId,
  planId,
  mutation,
  title = "Add Influencers",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [listId, setListId] = useState(defaultListId ?? lists[0]?.id ?? "");
  const [platform, setPlatform] = useState("instagram");
  const [rawInput, setRawInput] = useState("");
  // Holds the pending submission + the plan-wide duplicates it turned up, while
  // we wait for the user to say "add anyway" or cancel — see handleSubmit.
  const [duplicateConfirm, setDuplicateConfirm] = useState(null);

  const addToPlan = useAddInfluencers(planId);
  const addMutation = mutation ?? addToPlan;
  const { mutate } = addMutation;
  const isPending = isMutationPending(addMutation);
  const { enqueueSnackbar } = useSnackbar();

  // In-flight guard for the two submit buttons. `isPending` (see mutationState.js — it was
  // previously read under a name that doesn't exist on this app's react-query version, so
  // the buttons never disabled) only takes effect after React re-renders, so a fast double
  // click can still land both clicks first, each firing its own paid add request. A ref
  // flips synchronously, so the second click is dropped on the spot.
  // (A trailing-edge debounce would only delay the request and still let both clicks through;
  // this is leading-edge: the first click goes out immediately, the rest are ignored.)
  const submitLock = useRef(false);
  // Safety net: whatever happened to the request (settled, errored, or the callbacks never
  // ran), the lock is always released once the mutation is no longer pending.
  useEffect(() => {
    if (!isPending) submitLock.current = false;
  }, [isPending]);

  const parsedProfiles = useMemo(
    () => parseProfilesInput(rawInput, platform),
    [rawInput, platform]
  );

  const selectedList = useMemo(
    () => lists.find((l) => l.id === listId),
    [lists, listId]
  );
  // A list accepts only its own platform, so picking a list selects that platform instead of
  // leaving the toggle on Instagram and showing a mismatch error.
  const listPlatform = selectedList?.platform;
  useEffect(() => {
    if (listPlatform) setPlatform(listPlatform);
  }, [listPlatform]);

  const platformMismatch = Boolean(listPlatform) && listPlatform !== platform;

  const isValid = Boolean(listId) && parsedProfiles.length > 0 && !platformMismatch;

  const resetAndClose = () => {
    setRawInput("");
    setPlatform(listPlatform || "instagram");
    setDuplicateConfirm(null);
    onClose();
  };

  const handleSubmit = () => {
    if (!isValid || isPending || submitLock.current) return;
    submitLock.current = true;
    mutate(
      { listId, platform, profiles: parsedProfiles },
      {
        // The backend resolves each handle independently and can partially
        // fail (invalid username, private/nonexistent account, etc.) — report
        // what actually happened instead of assuming every pasted profile made it in.
        onSuccess: (data) => {
          const addedCount = data?.added?.length ?? 0;
          const failed = data?.failed ?? [];
          const duplicatesInPlan = data?.duplicatesInPlan ?? [];
          const extraReasons = failed.length > 1 ? ` (+${failed.length - 1} more)` : "";

          if (failed.length > 0) {
            if (addedCount === 0) {
              enqueueSnackbar(
                `Couldn't add any profiles — ${failed[0].reason}${extraReasons}`,
                { variant: "error" }
              );
            } else {
              enqueueSnackbar(
                `${addedCount} profile${addedCount === 1 ? "" : "s"} added, ${failed.length} skipped — ${failed[0].reason}${extraReasons}`,
                { variant: "warning" }
              );
            }
          } else if (addedCount > 0) {
            enqueueSnackbar(
              `${addedCount} profile${addedCount === 1 ? "" : "s"} added successfully.`,
              { variant: "success" }
            );
          }

          // Some profiles are already elsewhere in this plan — hold the drawer
          // open and ask before adding them here too, instead of silently
          // skipping (today) or silently duplicating (if we just added them).
          if (duplicatesInPlan.length > 0) {
            setDuplicateConfirm({ listId, platform, profiles: parsedProfiles, duplicates: duplicatesInPlan });
            return;
          }

          // Only reset/close on real progress — if nothing was added, leave the
          // drawer open with the input intact so the user can fix and retry.
          if (addedCount > 0) {
            resetAndClose();
          }
        },
        onError: (error) => {
          const message =
            error?.response?.data?.message ||
            "Couldn't add these influencers. Try again.";
          enqueueSnackbar(message, { variant: "error" });
        },
        onSettled: () => {
          submitLock.current = false;
        },
      }
    );
  };

  const handleCancelDuplicates = () => setDuplicateConfirm(null);

  const handleConfirmDuplicates = () => {
    if (!duplicateConfirm || isPending || submitLock.current) return;
    submitLock.current = true;
    const { listId: confirmListId, platform: confirmPlatform, profiles: confirmProfiles, duplicates } = duplicateConfirm;
    setDuplicateConfirm(null);

    mutate(
      {
        listId: confirmListId,
        platform: confirmPlatform,
        profiles: confirmProfiles,
        confirmedUsernames: duplicates.map((d) => d.username),
      },
      {
        onSuccess: (data) => {
          const addedCount = data?.added?.length ?? 0;
          enqueueSnackbar(
            `${addedCount} profile${addedCount === 1 ? "" : "s"} added.`,
            { variant: "success" }
          );
          resetAndClose();
        },
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Couldn't add these influencers. Try again.",
            { variant: "error" }
          );
        },
        onSettled: () => {
          submitLock.current = false;
        },
      }
    );
  };

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={resetAndClose}
      transitionDuration={{ enter: 300, exit: 250 }}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : { sm: 440, md: 500 },
          maxWidth: "100vw",
          borderRadius: isMobile ? 0 : "16px 0 0 16px",
          my: isMobile ? 0 : 2,
          mb: isMobile ? 0 : 2,
          height: isMobile ? "100%" : "calc(100% - 32px)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2.5,
            borderBottom: "1px solid #F3F4F6",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#111827" }}>
            {title}
          </Typography>
          <IconButton
            onClick={resetAndClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              background: "#F9FAFB",
              border: "1px solid #E5E7EB",
              "&:hover": { background: "#F3F4F6", color: "#374151" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
          <Typography sx={sectionLabelSx}>Choose a list</Typography>
          <Select
            fullWidth
            value={listId}
            onChange={(e) => setListId(e.target.value)}
            displayEmpty
            sx={{
              mb: 3,
              background: "#F9FAFB",
              borderRadius: "10px",
              fontSize: 14.5,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#D1D5DB" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FF5200", borderWidth: "1.5px" },
            }}
          >
            {lists.length === 0 && (
              <MenuItem value="" disabled>
                No lists yet — create one first
              </MenuItem>
            )}
            {lists.map((list) => (
              <MenuItem key={list.id} value={list.id}>
                {list.name}
                {list.platform && (
                  <Typography component="span" sx={{ ml: 1, color: "#9CA3AF", fontSize: 12.5 }}>
                    ({platformLabel(list.platform)} only)
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>

          <Typography sx={sectionLabelSx}>Choose a platform</Typography>
          <ToggleButtonGroup
            exclusive
            value={platform}
            onChange={(_, value) => value && setPlatform(value)}
            sx={platformGroupSx}
          >
            {PLAN_PLATFORMS.map(({ key, label, Icon, color }) => (
              <ToggleButton key={key} value={key} sx={platformButtonSx}>
                {/* Brand colour when idle; white on the selected (dark) tile. */}
                <Icon sx={{ fontSize: 22, color: platform === key ? "inherit" : color }} />
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {platformMismatch && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                mb: 3,
                p: 1.5,
                borderRadius: "10px",
                bgcolor: "rgba(239,68,68,0.06)",
                border: "1px solid rgba(239,68,68,0.2)",
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 18, color: "error.main", mt: 0.1, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 13, color: "#B91C1C" }}>
                <strong>{selectedList.name}</strong> is a {platformLabel(selectedList.platform)}-only
                list. Switch to {platformLabel(selectedList.platform)} above, or choose a different list.
              </Typography>
            </Box>
          )}

          <Typography sx={{ ...sectionLabelSx, mt: platformMismatch ? 0 : 1.5 }}>Add Influencers</Typography>
          <TextField
            fullWidth
            multiline
            value={rawInput}
            onChange={(e) => setRawInput(e.target.value)}
            placeholder={getPlatform(platform)?.placeholder}
            sx={{
              flex: 1,
              display: "flex",
              "& .MuiInputBase-root": {
                height: "100%",
                alignItems: "flex-start",
                background: "#F9FAFB",
                borderRadius: "10px",
                fontSize: 14,
              },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#D1D5DB" },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FF5200", borderWidth: "1.5px" },
            }}
          />
          {rawInput.trim() && (
            <Box
              sx={{
                mt: 1.5,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                alignSelf: "flex-start",
                px: 1.25,
                py: 0.5,
                borderRadius: "999px",
                bgcolor: "rgba(9,172,49,0.08)",
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 15, color: "success.main" }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: "success.dark" }}>
                {parsedProfiles.length} profile{parsedProfiles.length === 1 ? "" : "s"} detected
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            px: 3,
            py: 2,
            borderTop: "1px solid #F3F4F6",
            background: "#FAFAFA",
          }}
        >
          <Button
            fullWidth
            onClick={resetAndClose}
            sx={{
              color: "#6B7280", background: "#fff", borderRadius: "10px",
              py: 1.1, fontSize: 14, fontWeight: 500, textTransform: "none",
              border: "1px solid #E5E7EB", boxShadow: "none",
              "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB", color: "#374151" },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={!isValid || isPending}
            onClick={handleSubmit}
            sx={{
              borderRadius: "10px", py: 1.1, fontSize: 14, fontWeight: 700,
              textTransform: "none",
              background: isValid ? "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)" : undefined,
              boxShadow: isValid ? "0 4px 14px rgba(255,82,0,0.3)" : "none",
              "&:hover": {
                background: isValid ? "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)" : undefined,
                opacity: isValid ? 0.9 : 1,
                boxShadow: isValid ? "0 6px 20px rgba(255,82,0,0.45)" : "none",
              },
              // While the request runs keep the button in its active colour (spinner + "Adding…")
              // so the click visibly registered, instead of greying out like an invalid form.
              "&.Mui-disabled": isPending
                ? { background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)", color: "#fff", opacity: 0.85 }
                : { background: "#F3F4F6", color: "#9CA3AF" },
            }}
          >
            {isPending ? (
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} color="inherit" /> Adding…
              </Box>
            ) : title}
          </Button>
        </Box>
      </Box>
    </Drawer>

    <Dialog
      open={Boolean(duplicateConfirm)}
      onClose={handleCancelDuplicates}
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Already in another list?</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          {duplicateConfirm?.duplicates.length === 1 ? "This influencer is" : "These influencers are"}{" "}
          already in another list in this plan:
          <Box component="ul" sx={{ mt: 1, mb: 1.5, pl: 2.5 }}>
            {duplicateConfirm?.duplicates.map((d) => (
              <Box component="li" key={d.username} sx={{ fontSize: 14 }}>
                <strong>{d.name || d.username}</strong> — already in "{d.existingListName}"
              </Box>
            ))}
          </Box>
          Add {duplicateConfirm?.duplicates.length === 1 ? "this influencer" : "them"} to{" "}
          "{lists.find((l) => l.id === duplicateConfirm?.listId)?.name}" as well?
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleCancelDuplicates}
          sx={{ textTransform: "none", borderRadius: "10px" }}
        >
          Don't add
        </Button>
        <Button
          onClick={handleConfirmDuplicates}
          disabled={isPending}
          variant="contained"
          sx={{ textTransform: "none", borderRadius: "10px", boxShadow: "none" }}
        >
          {isPending ? (
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} color="inherit" /> Adding…
            </Box>
          ) : "Add anyway"}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AddInfluencersDrawer;
